import React, { useEffect, useState } from 'react';
import { SeatsioSeatingChart } from '@seatsio/seatsio-react/src/main';
import { withRouter } from 'react-router-dom';
import { SmartButtonStub } from 'ui/smart-buttons'

const SeatedEventReservationBlock = (props) => {
  // State
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true) // Set loading to true initially
  const [selectedSeats, setSelectedSeats] = useState({})
  const [seatsioEventId, setSeatsioEventId] = useState('')
  const [ticketSettings, setTicketSettings] = useState([])
  const [tickets, setTickets] = useState({})
  const [workspaceKey, setWorkspaceKey] = useState('')

  // Consts
  // Calculate total price for each category
  const seatCategories = Object.keys(selectedSeats).map(category => {
    const seatCount = selectedSeats[category].length;
    const seatPriceCents = ticketSettings.find(setting => setting.type === category)?.amount || 0;
    const seatPriceDollars = seatPriceCents / 100;  // Convert price from cents to dollars
    const totalPrice = seatPriceDollars * seatCount;
    return { category, seats: selectedSeats[category], seatCount, totalPrice };
  });
  const seatingChartPricing = ticketSettings.map(setting => ({
    category: setting.type,
    price: setting.amount ? setting.amount/100 : 0
  }));

  const subtotal = seatCategories.reduce((sum, category) => sum + category.totalPrice, 0);

  // Hooks
  useEffect(() => {
    const { eventId, token } = getParamsFromURL();
    if (eventId && token) {
      fetchEventDetails(eventId, token);
    } else {
      setError('Missing event ID or token in URL.')
      setLoading(false)
    }
  }, [])

  // Custom Methods
  const cancelReservation = () => {
    const { eventId, token } = getParamsFromURL();
    setSelectedSeats({})
    setTickets({})
    // Update the URL to include cancel and event ID
    props.history.push(`/event/${eventId}/cancel/${token}/event:${eventId}`);
  };

  const confirmSeats = () => {
    const { eventId, token } = getParamsFromURL();
    const { selectedSeats, tickets } = state;

    // Serialize and encode the selectedSeats and tickets objects
    const seatsParam = encodeURIComponent(JSON.stringify(selectedSeats));
    const spotsParam = encodeURIComponent(JSON.stringify(tickets));

    props.history.push(`/event/${eventId}/reserve/${token}/seats:${seatsParam}/spots:${spotsParam}`);
  };

  const fetchEventDetails = async (eventId, token) => {
    try {
      const response = await Backend.get('/events/fetch_seatsio_deatils', { event_id: eventId, token: token });
      const { event_id, workspace_key, ticket_settings } = response;
      if (event_id && workspace_key) {
        setSeatsioEventId(event_id)
        setWorkspaceKey(workspace_key)
        setTicketSettings(ticket_settings)
        setLoading(false)
      } else {
        setError('Invalid event data received.')
        setLoading(false)
      }
    } catch (error) {
      console.error('Error fetching event details:', error);
      setError('Failed to load event details.')
      setLoading(false)
    }
  };

  const getParamsFromURL = () => {
    const hash = window.location.hash.substring(1); // Remove the '#' character
    const parts = hash.split('/');

    // Validate URL structure
    if (parts.length < 5 || parts[1] !== 'event' ) {
      return { eventId: null, token: null };
    }

    const eventId = parts[2];
    const token = parts[4];

    return { eventId, token };
  };

  const onSeatDeselected = (seat) => {
    // Decrement the ticket count for the deselected seat's category, ensuring it doesn't go below 0
    if (tickets[seat.category.label] > 0) {
      tickets[seat.category.label] -= 1;

      // If the count becomes zero, remove the ticket category from the tickets object
      if (tickets[seat.category.label] === 0) {
        delete tickets[seat.category.label];
      }
    }

    // Remove the deselected seat from the correct category in selectedSeats
    selectedSeats[seat.category.label] = selectedSeats[seat.category.label].filter(selectedSeat => selectedSeat !== seat.seatId);

    // If no seats remain in this category, remove the category from selectedSeats
    if (selectedSeats[seat.category.label].length === 0) {
      delete selectedSeats[seat.category.label];
    }

    setTickets(tickets)
    setSelectedSeats(selectedSeats)
  }

  const onSeatSelected = (seat) => {
    // Increment the ticket count for the selected seat's category
    tickets[seat.category.label] = (tickets[seat.category.label] || 0) + 1;
    selectedSeats[seat.category.label] = [...(selectedSeats[seat.category.label] || []), seat.seatId];
    setTickets(tickets)
    setSelectedSeats(selectedSeats)
  }

  if (loading) {
    return <div className="loading">Loading seating chart...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="SeatedEventReservation-form">
      <h3 className="SeatedEventReservation-heading">Reservation Details</h3>
      <div className="seating-chart-container">
        <SeatsioSeatingChart
          workspaceKey={workspaceKey}
          event={seatsioEventId}
          region="na"
          onObjectSelected={onSeatSelected}
          onObjectDeselected={onSeatDeselected}
          showFullScreenButton={false}
          pricing={seatingChartPricing}
          showLegend={true}  // Show/hide legend if needed
          priceFormatter= {function(price) {
              return '$' + price;
          }}
          chartRendered={chart => {
            chart.zoomTo(2);  // Zoom to 2x level after the chart is rendered
          }}
        />
      </div>
      {(
        <div className="EventReservation-selected-seats-summary">
          <h4 className="EventReservation-selected-seats-summary-title">Selected Seats</h4>

          {seatCategories.length > 0 ? (
            <ul className="seat-list">
              {seatCategories.map((seat, index) => (
                <li key={index} className="seat-item">
                  <span className="seat-number">{seat.category} : {seat.seats.join(', ')}</span>
                  {seat.totalPrice > 0 && <span className="seat-price">Price: ${seat.totalPrice.toFixed(2)}</span>}
                </li>
              ))}
            </ul>
          ) : (
            <p className="EventReservation-selected-seats-summary-no-seats-selected">No seats selected.</p>
          )}

          <div className="EventReservation-selected-seats-summary-total-summary">
            <span className="total-label">Total:</span>
            <span className="total-amount">
            ${subtotal.toFixed(2)}
            </span>
          </div>
        </div>
      )}
      <div className="EventReservation-action">
        <SmartButtonStub
          className='mb-3'
          text="Confirm Reservation"
          click={confirmSeats}
          disabled={selectedSeats.length === 0}
        />
        <SmartButtonStub
          className='mb-3'
          text="Back"
          click={cancelReservation}
        />
      </div>
    </div>
  );
}

export default withRouter(SeatedEventReservationBlock);
